import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  LABEL_CATEGORIE_CHARGE,
  formatDate,
  formatEuro,
  isoToDateInput,
  parseDateToIso,
  parseEuroToCentimes,
} from '../lib/format';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Select } from '../components/ui/Select';
import type { CategorieChargeFixe } from '@cockpit/shared';

const CATEGORIES: CategorieChargeFixe[] = [
  'Loyer',
  'Salaires',
  'Cotisations_sociales',
  'Comptable',
  'Telecom',
  'Assurance',
  'Carburant',
  'Abonnements_logiciels',
  'Frais_bancaires',
  'Autres',
];

export function TresorerieChargesFixesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [actif, setActif] = useState(true);
  const [creerOpen, setCreerOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['charges-fixes', { actif }],
    queryFn: () => api.chargesFixes.list({ actif: actif ? 'true' : 'false' }),
  });

  const desactiver = useMutation({
    mutationFn: (id: number) => api.chargesFixes.desactiver(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['charges-fixes'] });
    },
  });

  return (
    <div className="max-w-7xl space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Charges fixes</h1>
          <p className="text-xs text-slate-500 mt-1">
            Sorties récurrentes mensuelles, utilisées pour le cash flow prévisionnel.
          </p>
        </div>
        <Button onClick={(): void => setCreerOpen(true)}>Ajouter une charge</Button>
      </header>

      <label className="inline-flex items-center gap-2 text-sm text-slate-300">
        <input
          type="checkbox"
          checked={actif}
          onChange={(e): void => setActif(e.target.checked)}
          className="rounded border-slate-600 bg-slate-800"
        />
        Actives uniquement
      </label>

      {isLoading ? (
        <p className="text-sm text-slate-500">Chargement…</p>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/50 border-b border-slate-800">
              <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                <th className="py-2 px-3">Libellé</th>
                <th className="py-2 px-3">Catégorie</th>
                <th className="py-2 px-3 text-right">Montant TTC</th>
                <th className="py-2 px-3">Jour</th>
                <th className="py-2 px-3">Depuis</th>
                <th className="py-2 px-3">Fin</th>
                <th className="py-2 px-3">Actif</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((c) => (
                <tr key={c.id} className="border-b border-slate-800/50">
                  <td className="py-2 px-3">{c.libelle}</td>
                  <td className="py-2 px-3 text-slate-400">
                    {LABEL_CATEGORIE_CHARGE[c.categorie] ?? c.categorie}
                  </td>
                  <td className="py-2 px-3 text-right">{formatEuro(c.montant_centimes)}</td>
                  <td className="py-2 px-3">{c.jour_du_mois}</td>
                  <td className="py-2 px-3 text-slate-400">{formatDate(c.date_debut)}</td>
                  <td className="py-2 px-3 text-slate-400">
                    {c.date_fin !== null ? formatDate(c.date_fin) : '—'}
                  </td>
                  <td className="py-2 px-3">
                    {c.actif ? (
                      <span className="text-emerald-300 text-xs">Oui</span>
                    ) : (
                      <span className="text-slate-500 text-xs">Non</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {c.actif && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(): void => desactiver.mutate(c.id)}
                      >
                        Désactiver
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {data?.items.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-sm text-slate-500">
                    Aucune charge fixe.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <CreerChargeModal isOpen={creerOpen} onClose={(): void => setCreerOpen(false)} />
    </div>
  );
}

function CreerChargeModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [libelle, setLibelle] = useState('');
  const [categorie, setCategorie] = useState<CategorieChargeFixe>('Autres');
  const [montant, setMontant] = useState('');
  const [jour, setJour] = useState('1');
  const [dateDebut, setDateDebut] = useState(isoToDateInput(new Date().toISOString()));
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const ct = parseEuroToCentimes(montant);
      const jourN = Number.parseInt(jour, 10);
      const dateIso = parseDateToIso(dateDebut);
      if (libelle.trim() === '') throw new Error('Libellé requis');
      if (ct === null || ct < 0) throw new Error('Montant invalide');
      if (!Number.isFinite(jourN) || jourN < 1 || jourN > 28) {
        throw new Error('Jour du mois entre 1 et 28');
      }
      if (dateIso === null) throw new Error('Date début invalide');
      return api.chargesFixes.create({
        libelle: libelle.trim(),
        categorie,
        montant_centimes: ct,
        jour_du_mois: jourN,
        date_debut: dateIso,
        date_fin: null,
        notes: null,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['charges-fixes'] });
      setLibelle('');
      setMontant('');
      setJour('1');
      onClose();
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : 'Erreur'),
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Ajouter une charge fixe"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={(): void => {
              setError(null);
              mutation.mutate();
            }}
            disabled={mutation.isPending}
          >
            Créer
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input label="Libellé" value={libelle} onChange={(e): void => setLibelle(e.target.value)} />
        <Select
          label="Catégorie"
          value={categorie}
          onChange={(e): void => setCategorie(e.target.value as CategorieChargeFixe)}
          options={CATEGORIES.map((c) => ({
            value: c,
            label: LABEL_CATEGORIE_CHARGE[c] ?? c,
          }))}
        />
        <Input
          label="Montant TTC (€)"
          value={montant}
          onChange={(e): void => setMontant(e.target.value)}
          placeholder="1 200,00"
        />
        <Input
          label="Jour du mois (1-28)"
          type="number"
          min="1"
          max="28"
          value={jour}
          onChange={(e): void => setJour(e.target.value)}
        />
        <Input
          label="Date de début"
          type="date"
          value={dateDebut}
          onChange={(e): void => setDateDebut(e.target.value)}
        />
        {error !== null && <p className="text-xs text-red-400">{error}</p>}
      </div>
    </Modal>
  );
}
